/*
 * Контурные иконки вместо текстовых глифов.
 *
 * До 20.09 на лендинге стояли ♡ 💬 ⚭ 🛡, разделитель ❖ и стрелки ▷ →.
 * Половина рендерилась цветными эмодзи, половина — монохромными знаками
 * шрифта, а ⚭ (U+26AD) отсутствует в системных шрифтах многих Android
 * и показывался пустым квадратом. Здесь всё в одном штрихе и наследует
 * currentColor, поэтому цвет задаётся токеном на родителе.
 */
import type { ReactElement, SVGProps } from 'react';

export type IconName =
  | 'spheres'
  | 'differences'
  | 'map'
  | 'privacy'
  | 'arrow'
  | 'play'
  | 'diamond'
  | 'menu'
  | 'close'
  | 'dot';

const PATHS: Record<IconName, ReactElement> = {
  spheres: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.4" />
      <path d="M12 3v5.6M12 15.4V21M3 12h5.6M15.4 12H21" />
    </>
  ),
  differences: (
    <>
      <circle cx="9" cy="12" r="6" />
      <circle cx="15" cy="12" r="6" />
    </>
  ),
  map: (
    <>
      <path d="M9 4 3 6.6v13L9 17l6 2.6 6-2.6v-13L15 6.6z" />
      <path d="M9 4v13M15 6.6v13" />
    </>
  ),
  privacy: (
    <>
      <path d="M12 3l7 2.9v5.4c0 4.2-2.9 7.7-7 9.4-4.1-1.7-7-5.2-7-9.4V5.9z" />
      <path d="M9.3 12.2l2 2 3.5-3.8" />
    </>
  ),
  arrow: <path d="M4 12h14M13 6l6 6-6 6" />,
  play: <path d="M9 6.5l8.5 5.5L9 17.5z" />,
  diamond: <path d="M12 3.5 20.5 12 12 20.5 3.5 12z" />,
  menu: <path d="M4 8h16M4 16h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  /* Метка стороны в легенде отчёта: раньше стоял символ ●, который
     в разных шрифтах имеет разный кегль и съезжает по базовой линии. */
  dot: <circle cx="12" cy="12" r="5.5" fill="currentColor" stroke="none" />,
};

type Props = SVGProps<SVGSVGElement> & {
  name: IconName;
  /** Размер стороны в пикселях. По умолчанию под кегль основного текста. */
  size?: number;
};

export function Icon({ name, size = 20, ...rest }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}

/*
 * Қошқар мүйіз — бараний рог, основной мотив сырмақа. Четыре рога зеркальны
 * друг другу относительно центра, поэтому рисуется один путь и отражается
 * трижды. Классы d-* раздают задержки: линия прочерчивается по порядку,
 * как шьют половины ковра. Анимация и prefers-reduced-motion — в app.css.
 */
export function Ornament() {
  return (
    <svg
      className="hero-orn"
      viewBox="-150 -150 300 300"
      fill="none"
      stroke="var(--brand-text)"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <path
          id="neke-horn"
          d="M0 -18C0 -60 -34 -84 -68 -72 -100 -60 -98 -14 -68 -8c20 4 28 -14 18 -24"
        />
      </defs>
      <path
        className="ink d-frame"
        strokeOpacity={0.62}
        d="M-128 -128h80l14 14h68l14 -14h80v80l-14 14v68l14 14v80h-80l-14 -14h-68l-14 14h-80v-80l14 -14v-68l-14 -14z"
      />
      <use className="ink d-1" href="#neke-horn" />
      <use className="ink d-2" href="#neke-horn" transform="scale(-1,1)" />
      <use className="ink d-3" href="#neke-horn" transform="scale(1,-1)" />
      <use className="ink d-4" href="#neke-horn" transform="scale(-1,-1)" />
      <path className="ink d-rhomb" d="M0 -46 46 0 0 46 -46 0z" />
      <path className="core" d="M0 -24 24 0 0 24 -24 0z" fill="var(--brand-text)" stroke="none" />
    </svg>
  );
}
